import React, { useState } from 'react';
import { X, Zap, Save, Send } from 'lucide-react';
import { useLanguage } from '../../../contexts/LanguageContext';

const QuickListModal = ({ onClose, onSave }) => {
  const { t } = useLanguage();
  const [form, setForm] = useState({
    address: '', price: '', bedrooms: '', bathrooms: '', type: 'house', description: '', saveAsDraft: false
  });
  const [saving, setSaving] = useState(false);

  const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }));
  const isValid = form?.address?.trim() && form?.price?.trim();

  const handleSave = (asDraft) => {
    setSaving(true);
    setTimeout(() => { onSave({ ...form, saveAsDraft: asDraft }); setSaving(false); }, 600);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(8px)' }}>
      <div className="w-full max-w-lg rounded-2xl overflow-hidden" style={{
        background: '#0A1628',
        border: '1px solid rgba(0,212,255,0.25)',
        boxShadow: '0 0 60px rgba(0,212,255,0.15)'
      }}>
        <div className="flex items-center justify-between p-6" style={{ borderBottom: '1px solid rgba(0,212,255,0.12)' }}>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{
              background: 'rgba(0,212,255,0.15)', border: '1px solid rgba(0,212,255,0.3)'
            }}>
              <Zap size={16} color="#00D4FF" />
            </div>
            <div>
              <h2 className="text-base font-bold" style={{ color: '#E8F4FD' }}>{t('quickListTitle')}</h2>
              <p className="text-xs" style={{ color: '#4A7A9B' }}>{t('quickListDesc')}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg" style={{ color: '#4A7A9B' }}
            onMouseEnter={e => { e.currentTarget.style.color = '#E8F4FD'; e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; }}
            onMouseLeave={e => { e.currentTarget.style.color = '#4A7A9B'; e.currentTarget.style.background = 'transparent'; }}
          ><X size={18} /></button>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <label className="block text-xs font-semibold mb-1.5" style={{ color: '#6B9EC4' }}>{t('propertyAddressRequired')}</label>
            <input type="text" placeholder="e.g. 42 Harbour View Drive, Sydney NSW 2000"
              value={form?.address} onChange={e => update('address', e?.target?.value)}
              className="w-full px-4 py-3 rounded-xl text-sm outline-none"
              style={{ background: 'rgba(0,212,255,0.06)', border: `1px solid ${form?.address ? 'rgba(0,212,255,0.35)' : 'rgba(0,212,255,0.15)'}`, color: '#E8F4FD' }}
            />
          </div>

          <div>
            <label className="block text-xs font-semibold mb-1.5" style={{ color: '#6B9EC4' }}>{t('askingPriceRequired')}</label>
            <input type="text" placeholder="e.g. $850,000 or AED 2,400,000"
              value={form?.price} onChange={e => update('price', e?.target?.value)}
              className="w-full px-4 py-3 rounded-xl text-sm outline-none"
              style={{ background: 'rgba(0,212,255,0.06)', border: `1px solid ${form?.price ? 'rgba(0,212,255,0.35)' : 'rgba(0,212,255,0.15)'}`, color: '#E8F4FD' }}
            />
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-semibold mb-1.5" style={{ color: '#6B9EC4' }}>{t('bedrooms')}</label>
              <input type="number" min="0" placeholder="3" value={form?.bedrooms}
                onChange={e => update('bedrooms', e?.target?.value)}
                className="w-full px-3 py-3 rounded-xl text-sm outline-none"
                style={{ background: 'rgba(0,212,255,0.06)', border: '1px solid rgba(0,212,255,0.15)', color: '#E8F4FD' }}
              />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1.5" style={{ color: '#6B9EC4' }}>{t('bathrooms')}</label>
              <input type="number" min="0" placeholder="2" value={form?.bathrooms}
                onChange={e => update('bathrooms', e?.target?.value)}
                className="w-full px-3 py-3 rounded-xl text-sm outline-none"
                style={{ background: 'rgba(0,212,255,0.06)', border: '1px solid rgba(0,212,255,0.15)', color: '#E8F4FD' }}
              />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1.5" style={{ color: '#6B9EC4' }}>{t('type')}</label>
              <select value={form?.type} onChange={e => update('type', e?.target?.value)}
                className="w-full px-3 py-3 rounded-xl text-sm outline-none"
                style={{ background: 'rgba(0,212,255,0.06)', border: '1px solid rgba(0,212,255,0.15)', color: '#E8F4FD' }}
              >
                <option value="house">{t('house')}</option>
                <option value="unit">Unit</option>
                <option value="commercial">{t('commercialLabel')}</option>
                <option value="land">Land</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold mb-1.5" style={{ color: '#6B9EC4' }}>{t('shortDescriptionOptional')}</label>
            <textarea rows={3} placeholder="Brief property description — AI will enhance this automatically..."
              value={form?.description} onChange={e => update('description', e?.target?.value)}
              className="w-full px-4 py-3 rounded-xl text-sm outline-none resize-none"
              style={{ background: 'rgba(0,212,255,0.06)', border: '1px solid rgba(0,212,255,0.15)', color: '#E8F4FD' }}
            />
          </div>

          <div className="flex items-start gap-2 p-3 rounded-xl" style={{ background: 'rgba(255,184,0,0.06)', border: '1px solid rgba(255,184,0,0.15)' }}>
            <Save size={13} color="#FFB800" className="flex-shrink-0 mt-0.5" />
            <p className="text-xs" style={{ color: '#A0C4D8' }}>{t('draftNote')}</p>
          </div>
        </div>

        <div className="flex items-center justify-between p-6" style={{ borderTop: '1px solid rgba(0,212,255,0.12)' }}>
          <button
            onClick={() => handleSave(true)}
            disabled={!isValid || saving}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all"
            style={{
              background: isValid ? 'rgba(255,184,0,0.12)' : 'rgba(255,255,255,0.04)',
              color: isValid ? '#FFB800' : '#4A7A9B',
              border: isValid ? '1px solid rgba(255,184,0,0.3)' : '1px solid rgba(255,255,255,0.08)',
              cursor: isValid ? 'pointer' : 'not-allowed'
            }}
          >
            <Save size={14} />
            {t('saveAsDraft')}
          </button>
          <button
            onClick={() => handleSave(false)}
            disabled={!isValid || saving}
            className="flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-semibold transition-all"
            style={{
              background: isValid ? 'linear-gradient(135deg, #00D4FF, #7B2FFF)' : 'rgba(0,212,255,0.1)',
              color: isValid ? '#fff' : '#4A7A9B',
              cursor: isValid ? 'pointer' : 'not-allowed',
              boxShadow: isValid ? '0 0 20px rgba(0,212,255,0.3)' : 'none'
            }}
          >
            <Send size={14} />
            {saving ? t('publishing') : t('publishLive')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default QuickListModal;
